import * as React from "react";
import { useState } from "react";
import {
  Grid,
  Link,
  InputLabel,
  Button,
  TextField,
  Typography,
  Switch,
  Box,
  Radio,
} from "@mui/material";
import { AuthenticationContext, ImagesContext } from "../Context";
import UnsavedDataConfirmation from "../modals/UnsavedDataConfirmation";
import TemplateChargeLine from "./TemplateChargeLine";
import { ChargeLine, ClaimTemplate } from "./types";
import { getHeaders } from "../utils/HeaderHelper";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useFlash } from "../FlashContext";

const MAX_TITLE_LENGTH = 100;
const MAX_SUBTEXT_LENGTH = 500;

const normalizeForSave = (value?: string): string => {
  if (!value) return "";
  return value.trim().replace(/\s+/g, " ");
};

const emptyClaimTemplate: ClaimTemplate = {
  id: null,
  title: "",
  subtext: "",
  require_prior_authorization_number: false,
  require_additional_provider: false,
  additional_provider_role: "referring",
};

const emptyChargeLine: ChargeLine = {
  id: null,
  cpt_code: "",
  units: "",
  modifiers: [],
  diagnosis_codes: [{ code: "", index: 0, id: null }],
  ndc_code: "",
  unit_modifier: "",
};

const NewClaimTemplate: React.FC = () => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const images = React.useContext(ImagesContext);
  const { showToast } = useFlash();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const viewMode = queryParams.get("view") === "true";
  const { id } = useParams();

  const [claimTemplate, setClaimTemplate] =
    React.useState<ClaimTemplate>(emptyClaimTemplate);

  const [chargeLines, setChargeLines] = React.useState<ChargeLine[]>([
    { ...emptyChargeLine },
  ]);

  const [existingClaimTemplate, setExistingClaimTemplate] =
    React.useState<any>(null);

  const isReadonly = claimTemplate.status === "published";

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [unsavedModalOpen, setUnsavedModalOpen] = useState<boolean>(false);

  const totalModifiers = chargeLines.reduce(
    (sum, charge) => sum + charge?.modifiers?.length,
    0
  );

  console.log("claimTemplate", claimTemplate);
  console.log("chargeLines", chargeLines);

  const totalDiagnosisCodes = chargeLines.reduce(
    (sum, charge) => sum + charge?.diagnosis_codes?.length,
    0
  );

  React.useEffect(() => {
    if (id) {
      getClaimTemplateData();
    }
  }, [id]);

  const getClaimTemplateData = () => {
    fetch(`/claim_templates/${id}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          alert(result.error);
        } else {
          const claimTemplate = result.resource;

          setExistingClaimTemplate(claimTemplate);

          console.log("claimTemplate", claimTemplate);
          setClaimTemplate({
            id: claimTemplate.id,
            title: claimTemplate.title,
            subtext: claimTemplate.subtext,
            require_prior_authorization_number:
              claimTemplate.prior_authorization_number_required,
            require_additional_provider:
              claimTemplate.additional_provider_required,
            additional_provider_role: claimTemplate.additional_provider_role,
            status: claimTemplate.status,
          });

          setChargeLines(
            claimTemplate?.template_charge_lines?.map((chargeLine: any) => ({
              id: chargeLine.id ?? null,
              cpt_code: chargeLine.cpt_code ?? "",
              units: chargeLine.units ?? "",
              modifiers: chargeLine.template_modifiers?.map(
                (m: any, index: number) => ({
                  id: m.id ?? null,
                  code: m.code,
                  index,
                })
              ) ?? [{ code: "", index: 0, id: null }],
              diagnosis_codes: chargeLine.template_diagnosis_codes?.map(
                (d: any, index: number) => ({
                  id: d.id ?? null,
                  code: d.code,
                  index,
                })
              ) ?? [{ code: "", index: 0, id: null }],
              ndc_code: chargeLine.ndc_code,
              unit_modifier: chargeLine.unit_modifier,
            })) ?? [{ ...emptyChargeLine }]
          );
        }
      })
      .catch((error) => {
        alert(error);
      });
  };

  const handleBackToList = () => {
    if (hasUnsavedChanges) {
      setUnsavedModalOpen(true);
    } else {
      window.location.href = "/claim-template-builder";
    }
  };

  const handleAddChargeLine = () => {
    if (chargeLines.length < 6) {
      const newChargeLine = {
        ...emptyChargeLine,
      };

      setChargeLines([...chargeLines, newChargeLine]);
    }
  };

  const handleRemoveChargeLine = (index: number) => {
    if (index === 0) return;
    const updated = [...chargeLines];
    updated.splice(index, 1);
    setChargeLines(updated);
  };

  const handleChange = <K extends keyof ChargeLine>(
    index: number,
    field: K,
    value: ChargeLine[K]
  ) => {
    const updated = [...chargeLines];
    updated[index][field] = value;
    setChargeLines(updated);
  };

  const handleSave = async (status: string) => {
    if (!claimTemplate.title) {
      showToast({
        title: "Title Required",
        text: "Title is required",
        type: "error",
      });
      return;
    }

    const endpoint = id ? `/claim_templates/${id}` : "/claim_templates";
    const method = id ? "PUT" : "POST";

    const originalChargeIds =
      existingClaimTemplate?.template_charge_lines?.map(
        (line: any) => line.id
      ) || [];

    const currentChargeIds = chargeLines
      .map((charge) => charge.id)
      .filter(Boolean);

    const chargesToDestroy = originalChargeIds.filter(
      (id) => !currentChargeIds.includes(id)
    );

    const chargeLinesData = chargeLines.map((charge) => {
      const chargeLineData: any = {
        cpt_code: normalizeForSave(charge.cpt_code),
        units: charge.units,
        ndc_code: normalizeForSave(charge.ndc_code),
        unit_modifier: normalizeForSave(charge.unit_modifier),
        template_modifiers_attributes: charge.modifiers.map(
          (modifier: any) => ({
            id: modifier.id || undefined,
            code: normalizeForSave(modifier.code),
            index: modifier.index,
            _destroy: modifier.code ? false : true,
          })
        ),
        template_diagnosis_codes_attributes: charge.diagnosis_codes.map(
          (diagnosis: any) => ({
            id: diagnosis.id || undefined,
            code: normalizeForSave(diagnosis.code),
            index: diagnosis.index,
            _destroy: diagnosis.code ? false : true,
          })
        ),
      };

      if (charge.id) {
        chargeLineData.id = charge.id;
        chargeLineData._destroy = chargesToDestroy.includes(charge.id);
      }

      return chargeLineData;
    });

    chargesToDestroy.forEach((id) => {
      if (!chargeLines.some((charge) => charge.id === id)) {
        chargeLinesData.push({
          id,
          _destroy: true,
        });
      }
    });

    const claimObject = {
      title: normalizeForSave(claimTemplate.title),
      subtext: normalizeForSave(claimTemplate.subtext),
      prior_authorization_number_required:
        claimTemplate.require_prior_authorization_number,
      additional_provider_required:
        claimTemplate.require_additional_provider,
      additional_provider_role: claimTemplate.require_additional_provider
        ? normalizeForSave(claimTemplate.additional_provider_role)
        : "",
    };

    await fetch(endpoint, {
      method,
      headers: {
        ...getHeaders(authenticationSetting.csrfToken),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        claim_template: {
          ...claimObject,
          template_charge_lines_attributes: chargeLinesData,
        },
      }),
    });

    showToast({
      title: "Claim Template Saved",
      text: "Claim template saved successfully",
      type: "success",
    });

    history.push(`/claim-template-builder`);
  };

  return (
    <>
      <UnsavedDataConfirmation
        open={unsavedModalOpen}
        closeModal={() => setUnsavedModalOpen(false)}
        redirectUrl="/claim-template-builder"
      />
      <div className="main-content-outer">
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          className="main-content"
          sx={{ paddingLeft: "138px" }}
        >
          <Grid
            item
            xs={12}
            id="provider-action-report"
            className="patient-edit-container patient-edit-form provider-action-report new-action-container"
          >
            <Grid container>
              <Grid
                className="patient-edit-header"
                container
                justifyContent="space-between"
              >
                <Grid item xs={4}>
                  <p className="secondary-label" style={{ marginLeft: "0px" }}>
                    Add Claim Template
                  </p>
                </Grid>

                <Grid item xs={6} className="q-btn-container">
                  <Link
                    style={{ marginRight: "16px" }}
                    onClick={handleBackToList}
                  >
                    Back to Template List
                  </Link>
                  {viewMode ||
                    (!isReadonly && (
                      <Button
                        className="orange-btn"
                        onClick={() => handleSave("draft")}
                        disabled={isReadonly}
                        style={{
                          height: "40px",
                          marginRight: "16px",
                          paddingLeft: "16px",
                          paddingRight: "16px",
                        }}
                      >
                        Save Draft
                      </Button>
                    ))}
                </Grid>
              </Grid>
              <Grid container sx={{ padding: "30px" }} wrap="nowrap">
                {/* column 1 */}
                <Grid item xs={3.5}>
                  <Grid container className="form-container">
                    <Grid
                      item
                      xs={12}
                      className="patient-info-left-container form-left-container"
                      style={{
                        marginLeft: "0px",
                      }}
                    >
                      <Grid container>
                        <Grid item xs={12}>
                          <InputLabel
                            htmlFor="name"
                            className="field-label"
                            style={{
                              color: "#000000",
                              fontWeight: 700,
                              marginTop: "0px",
                              paddingTop: "0px",
                            }}
                          >
                            Title*
                          </InputLabel>
                        </Grid>
                        <Grid item xs={12} className="field-container">
                         <TextField
                            id="title"
                            size="small"
                            value={claimTemplate.title}
                            className="the-field"
                            required
                            variant="outlined"
                            disabled={isReadonly}
                            onKeyUp={() => {
                              setHasUnsavedChanges(true);
                            }}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => {
                              const { value } = event.target;
                              if (value.length > MAX_TITLE_LENGTH) return;
                              setClaimTemplate({
                                ...claimTemplate,
                                title: value,
                              });
                            }}
                            placeholder="Enter Title"
                            inputProps={{ maxLength: MAX_TITLE_LENGTH }}
                            sx={{
                              backgroundColor: "#efe9e7 !important",
                              width: "100% !important",
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ marginTop: "5px" }}>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color: "black",
                            }}
                          >
                            *Required
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container>
                        <Grid item xs={12}>
                          <InputLabel
                            htmlFor="subtext"
                            className="field-label"
                            style={{ color: "#000000", fontWeight: 700 }}
                          >
                            Subtext
                          </InputLabel>
                        </Grid>
                        <Grid item xs={12} className="field-container">
                          <TextField
                            id="subtext"
                            size="small"
                            value={claimTemplate.subtext}
                            className="the-field"
                            variant="outlined"
                            disabled={isReadonly}
                            onKeyUp={() => {
                              setHasUnsavedChanges(true);
                            }}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                            ) => {
                              const { value } = event.target;
                              if (value.length > MAX_SUBTEXT_LENGTH) return;

                              setClaimTemplate({
                                ...claimTemplate,
                                subtext: value,
                              });
                            }}
                            placeholder="Enter Subtext"
                            inputProps={{ maxLength: MAX_SUBTEXT_LENGTH }}
                            sx={{
                              backgroundColor: "#efe9e7 !important",
                              width: "100% !important",
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Grid
                        container
                        sx={{ marginTop: "24px", marginBottom: "24px" }}
                      >
                        <Grid item xs={12}>
                          <Typography
                            sx={{
                              fontSize: "21px",
                              fontWeight: 700,
                              lineHeight: "16px",
                              color: "black",
                            }}
                          >
                            Template Details
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid
                        container
                        display="flex"
                        alignItems="center"
                        sx={{ marginBottom: "24px" }}
                      >
                        <Grid item xs={2}>
                          <Switch
                            checked={
                              claimTemplate.require_prior_authorization_number
                            }
                            onChange={(event) => {
                              setClaimTemplate({
                                ...claimTemplate,
                                require_prior_authorization_number:
                                  event.target.checked,
                              });
                            }}
                            disabled={isReadonly}
                            color="primary"
                            sx={{
                              marginLeft: "-8px",
                            }}
                          />
                        </Grid>
                        <Grid item xs={10}>
                          <Typography
                            sx={{
                              fontSize: "16px",
                              lineHeight: "22px",
                              color: "#1E1E1E",
                              fontWeight: 700,
                              marginLeft: "-15px",
                            }}
                          >
                            Require Prior Authorization Number
                          </Typography>
                        </Grid>

                        <Grid item xs={2}></Grid>
                        <Grid item xs={10}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              lineHeight: "18px",
                              color: "#707070",
                              fontWeight: 400,
                              marginLeft: "-15px",
                            }}
                          >
                            Enable this setting to require users to include a
                            prior authorization number before the claim can be
                            submitted.
                          </Typography>
                        </Grid>
                      </Grid>

                      <Grid container display="flex" alignItems="center">
                        <Grid item xs={2}>
                          <Switch
                            checked={claimTemplate.require_additional_provider}
                            onChange={(event) => {
                              setClaimTemplate({
                                ...claimTemplate,
                                require_additional_provider:
                                  event.target.checked,
                              });
                            }}
                            disabled={isReadonly}
                            color="primary"
                            sx={{
                              marginLeft: "-8px",
                            }}
                          />
                        </Grid>
                        <Grid item xs={10}>
                          <Typography
                            sx={{
                              fontSize: "16px",
                              lineHeight: "22px",
                              color: "#1E1E1E",
                              fontWeight: 700,
                              marginLeft: "-15px",
                            }}
                          >
                            Require Additional Provider
                          </Typography>
                        </Grid>

                        <Grid item xs={2}></Grid>
                        <Grid item xs={10}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              lineHeight: "18px",
                              color: "#707070",
                              fontWeight: 400,
                              marginLeft: "-15px",
                              marginBottom: "20px",
                            }}
                          >
                            Enable this setting to require users to include an
                            additional provider before the claim can be
                            submitted. Select the provider’s role to be
                            associated with the claim.
                          </Typography>
                        </Grid>

                        <Grid item xs={2}></Grid>
                        <Grid item xs={10}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              lineHeight: "22px",
                              color: "#000000",
                              fontWeight: 700,
                              marginLeft: "-18px",
                            }}
                          >
                            Select Provider Role
                          </Typography>

                          <div
                            className="row"
                            style={{ height: 40, marginLeft: "-31px" }}
                          >
                            <Radio
                              checked={
                                claimTemplate.additional_provider_role ===
                                "referring"
                              }
                              onChange={(event) => {
                                setClaimTemplate({
                                  ...claimTemplate,
                                  additional_provider_role: event.target.value,
                                });
                              }}
                              disabled={
                                !claimTemplate.require_additional_provider ||
                                isReadonly
                              }
                              value="referring"
                              name="radio-buttons"
                              inputProps={{ "aria-label": "referring" }}
                              style={{ backgroundColor: "transparent" }}
                              sx={{
                                color: "#A29D9B",
                                ml: 1,
                                width: 32,
                                height: 32,
                              }}
                            />
                            <p
                              className="radio-label"
                              style={{ whiteSpace: "nowrap" }}
                            >
                              Referring
                            </p>
                            <Radio
                              checked={
                                claimTemplate.additional_provider_role ===
                                "ordering"
                              }
                              onChange={(event) => {
                                setClaimTemplate({
                                  ...claimTemplate,
                                  additional_provider_role: event.target.value,
                                });
                              }}
                              disabled={
                                !claimTemplate.require_additional_provider ||
                                isReadonly
                              }
                              value="ordering"
                              name="radio-buttons"
                              inputProps={{ "aria-label": "ordering" }}
                              style={{ backgroundColor: "transparent" }}
                              sx={{
                                color: "#A29D9B",
                                ml: 1,
                                width: 32,
                                height: 32,
                              }}
                            />
                            <p
                              className="radio-label"
                              style={{ whiteSpace: "nowrap" }}
                            >
                              Ordering
                            </p>
                            <Radio
                              checked={
                                claimTemplate.additional_provider_role ===
                                "supervising"
                              }
                              onChange={(event) => {
                                setClaimTemplate({
                                  ...claimTemplate,
                                  additional_provider_role: event.target.value,
                                });
                              }}
                              disabled={
                                !claimTemplate.require_additional_provider ||
                                isReadonly
                              }
                              value="supervising"
                              name="radio-buttons"
                              inputProps={{ "aria-label": "supervising" }}
                              style={{ backgroundColor: "transparent" }}
                              sx={{
                                color: "#A29D9B",
                                ml: 1,
                                width: 32,
                                height: 32,
                              }}
                            />
                            <p className="radio-label">Supervising</p>
                          </div>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid
                  item
                  sx={{ flex: "0 0 2px", mx: "16px", alignSelf: "stretch" }}
                >
                  <Box
                    sx={{ width: "2px", height: "100%", bgcolor: "#EFE9E7" }}
                  />
                </Grid>

                {/* column 2 */}
                <Grid item xs={8.5} className="charge-line-column">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      rowGap: "16px",
                      backgroundColor: "#EFE9E7",
                      padding: "16px",
                    }}
                  >
                    {chargeLines.map((chargeLine, index) => (
                      <TemplateChargeLine
                        key={index + 1}
                        index={index}
                        chargeLine={chargeLine}
                        totalModifiers={totalModifiers}
                        totalDiagnosisCodes={totalDiagnosisCodes}
                        onChange={handleChange}
                        onRemove={handleRemoveChargeLine}
                        isReadonly={isReadonly}
                      />
                    ))}
                  </Box>

                  {chargeLines.length < 6 && (
                    <div
                      className="row"
                      style={{ marginTop: "16px", marginBottom: "16px" }}
                    >
                      <Link
                        className="action-link row"
                        onClick={handleAddChargeLine}
                        sx={{
                          pointerEvents: isReadonly ? "none" : "auto",
                        }}
                      >
                        <img
                          src={images.add_icon}
                          alt="Add Charge"
                          className="action-icon-image"
                          style={{ width: 31.466, maxHeight: 32 }}
                        />
                        <span className="app-user-text disable-pointer">
                          Add Charge Line
                        </span>
                      </Link>
                    </div>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default NewClaimTemplate;
